import React, { useEffect } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';

import styles from './Liquidation.module.scss';
import Button from '../../../../components/button/button';
import FormCheckbox from '../../../../components/formCheckbox/FormCheckbox';
import FormInput from '../../../../components/formInput/FormInput';
import Portal from '../../../../components/portal/Portal';
import StepperNavigation from '../../../../components/stepper/StepperNavigation';
import InputContainer from '../../../../components/template/InputContainer/InputContainer';
import Notes from '../../../../components/template/Notes/Notes';
import SectionContainer from '../../../../components/template/SectionContainer/SectionContainer';
import Subtitle from '../../../../components/template/Subtitle/Subtitle';
import Title from '../../../../components/template/Title/Title';
import { QuestionForm } from '../../../../models/questionModel';
import useFormStore, { Form } from '../../../../store/FormStore';
import useQuestionStore from '../../../../store/QuestionStore';
import useStepperStore, {
  defaultStepper,
} from '../../../../store/StepperStore';
import MainQuestion from '../MainQuestion/MainQuestion';

interface LiquidationProps {}

const Liquidation: React.FC<LiquidationProps> = ({}) => {
  const { setQuestion } = useQuestionStore();
  const { setStepper } = useStepperStore();
  const { setForm } = useFormStore();

  //#region Form
  const formMethods = useFormContext<Form>();
  const {
    getValues,
    reset,
    watch,
    formState: { isValid },
    setValue,
  } = formMethods;
  const { liquidation } = watch();
  //#endregion

  useEffect(() => {
    reset(getValues());
  }, [liquidation?.compulsory]);

  //#region Handler
  const getImpactQuestions = (
    impactTitle: 'Current Ratio' | 'Quick Ratio' | 'Cash Ratio'
  ) => {
    const currentRatio =
      Number(getValues('liquidation.currentRatio.assets')) /
      Number(getValues('liquidation.currentRatio.liabilities'));

    const quickRatio =
      (Number(getValues('liquidation.quickRatio.assets')) -
        Number(getValues('liquidation.quickRatio.inventory')) -
        Number(getValues('liquidation.quickRatio.prepayments'))) /
      Number(getValues('liquidation.quickRatio.liabilities'));

    const cashRatio =
      Number(getValues('liquidation.cashRatio.cashAndCashEquivalent')) /
      Number(getValues('liquidation.cashRatio.liabilities'));

    const notes =
      LiquidationData.impactQuestions.find((imp) => imp.title == impactTitle)
        ?.notes || [];

    let value = 0;

    if (impactTitle == 'Current Ratio') {
      value = currentRatio;
    } else {
      value = impactTitle == 'Quick Ratio' ? quickRatio : cashRatio;
    }

    const selectedNotes = notes.find((n) => value > n.min && value <= n.max);

    return {
      answer: `${value?.toFixed(1)}`,
      answerNotes: [selectedNotes?.label || ''],
      marks: selectedNotes?.marks || 0,
      question: impactTitle,
    };
  };

  const onHandleNavigation = (nav: 'next' | 'prev') => {
    if (!isValid) return;

    const compulsoryQuestions = LiquidationData.compulsoryQuestions.map(
      (comp) => {
        return {
          answer: getValues('liquidation.compulsory'),
          question: comp.question,
          questionNotes: [],
          answerNotes: [],
        };
      }
    );
    const impactQuestions = LiquidationData.impactQuestions.map((imp) => {
      return {
        ...getImpactQuestions(imp.title as any),
        questionNotes: [],
      };
    });
    const probabilityQuestions = LiquidationData.probabilityQuestions.map(
      (comp) => {
        return {
          answer:
            comp.inputs.find(
              (inp) => inp.value == Number(getValues('liquidation.probability'))
            )?.label || '',
          question: comp.question,
          questionNotes: [],
          answerNotes: [],
          marks: Number(getValues('liquidation.probability')),
        };
      }
    );
    const finalLiquidationData = new QuestionForm(
      LiquidationData.title,
      LiquidationData.description,
      compulsoryQuestions,
      impactQuestions,
      probabilityQuestions
    );

    setQuestion({ liquidation: finalLiquidationData });

    setForm({ ...getValues() });

    setStepper(
      nav,
      undefined,
      liquidation?.compulsory == ''
        ? undefined
        : liquidation?.compulsory == 'Yes'
    );
  };
  //#endregion

  const LiquidationData = {
    title: 'Liquidity Risk',
    description:
      'Liquidity risk occurs when an individual investor, business, or financial institution cannot meet its short-term debt obligations.',
    compulsoryQuestions: [
      {
        id: 'liquidation.compulsory',
        question: 'Is your Company exposed to liquidity risk?',
        inputs: [
          { value: 'Yes', label: 'Yes' },
          { value: 'Not applicable', label: 'Not applicable' },
        ],
      },
    ],
    probabilityQuestions: [
      {
        id: 'liquidation.probability',
        question: 'How frequent is the Company facing the risk?',
        inputs: [
          { value: 1, label: 'Improbable' },
          { value: 2, label: 'Remote' },
          { value: 3, label: 'Fairly Likely' },
          { value: 4, label: 'Probable' },
          { value: 5, label: 'Very Likely' },
        ],
      },
    ],
    impactQuestions: [
      {
        title: 'Current Ratio',
        objective:
          "Objective: Measures a company's ability to pay short term liabilities due within a year.",
        notes: [
          {
            min: -Infinity,
            max: 1,
            marks: 1,
            label:
              'Below 1.0: You might struggle to cover short-term liabilities. This isn’t always bad as growth-stage businesses often operate this way temporarily.',
          },
          {
            min: 1,
            max: 1.5,
            marks: 5,
            label:
              '1.0 to 1.5: You have enough liquidity to meet short-term obligations.',
          },
          {
            min: 1.5,
            max: Infinity,
            marks: 3,
            label:
              'Well above 1.5: You may have excess cash or assets that could be reinvested in the business.',
          },
        ],
        inputs: [
          { id: 'liquidation.currentRatio.assets', label: 'Current Assets' },
          {
            id: 'liquidation.currentRatio.liabilities',
            label: 'Current Liabilities',
          },
        ],
      },
      {
        title: 'Quick Ratio',
        objective:
          'Objective : Measures a company’s ability to quickly convert liquid assets into cash',
        notes: [
          {
            min: -Infinity,
            max: 1,
            marks: 1,
            label:
              'Below 1.0: You may need to consider current assets, or else needs to prepare in case this becomes a problem for continuing in business.',
          },
          {
            min: 1,
            max: 2,
            marks: 5,
            label:
              '1.0 to 2.0: The business has sufficient funds pay its debts.',
          },
          {
            min: 2,
            max: Infinity,
            marks: 3,
            label:
              'Well above 2.0: Looks attracting but it could also mean the business isn’t reinvesting its cash or putting it to use.',
          },
        ],
        inputs: [
          { id: 'liquidation.quickRatio.assets', label: 'Current Assets' },
          { id: 'liquidation.quickRatio.inventory', label: 'Inventory' },
          { id: 'liquidation.quickRatio.prepayments', label: 'Prepayments' },
          {
            id: 'liquidation.quickRatio.liabilities',
            label: 'Current Liabilities',
          },
        ],
      },
      {
        title: 'Cash Ratio',
        objective:
          'Objective : Measures the ability of the company to covert into easily convertible assets',
        notes: [
          {
            min: -Infinity,
            max: 1,
            marks: 1,
            label:
              'Below 1.0: You may need to have more liquid assets in order to pay current obligations.',
          },
          {
            min: 1,
            max: 2,
            marks: 5,
            label:
              '1.0 to 2.0: The Company has sufficient liquid assets to pay current obligations.',
          },
          {
            min: 2,
            max: Infinity,
            marks: 3,
            label: 'Well above 2.0: Your cash should be utilised better.',
          },
        ],
        inputs: [
          {
            id: 'liquidation.cashRatio.cashAndCashEquivalent',
            label: 'Cash and Cash Equivalent',
          },
          {
            id: 'liquidation.cashRatio.liabilities',
            label: 'Current Liabilities',
          },
        ],
      },
    ],
  };

  const generateRandomData = () => {
    const randomNumber = (min = 1, max = 10) =>
      Math.floor(Math.random() * (max - min + 1)) + min;

    return {
      compulsory: Math.random() > 0.5 ? 'Yes' : 'Not applicable',
      probability: randomNumber(1, 5),
      currentRatio: {
        assets: randomNumber(1, 10000).toString(),
        liabilities: randomNumber(1, 10000).toString(),
      },
      quickRatio: {
        assets: randomNumber(1, 10000).toString(),
        inventory: randomNumber(1, 10000).toString(),
        prepayments: randomNumber(1, 10000).toString(),
        liabilities: randomNumber(1, 10000).toString(),
      },
      cashRatio: {
        cashAndCashEquivalent: randomNumber(1, 10000).toString(),
        liabilities: randomNumber(1, 10000).toString(),
      },
    };
  };

  return (
    <FormProvider {...formMethods}>
      <div className={styles.container}>
        <MainQuestion
          id="liquidation"
          title={LiquidationData.title}
          description={LiquidationData.description}
          compulsoryQuestions={LiquidationData.compulsoryQuestions}
          probabilityQuestions={LiquidationData.probabilityQuestions}
        />

        {liquidation?.compulsory && liquidation?.compulsory == 'Yes' && (
          <SectionContainer>
            {LiquidationData.impactQuestions.map((impact, idx) => (
              <React.Fragment key={idx}>
                <Title>{impact.title}</Title>
                <Subtitle>{impact.objective}</Subtitle>

                <Notes key={idx}>
                  {impact.notes.map((n, idx) => (
                    <React.Fragment key={idx}>
                      {n.label} <br />
                    </React.Fragment>
                  ))}
                </Notes>

                <InputContainer>
                  {impact.inputs.map((inp, idx) => (
                    <FormInput
                      key={idx}
                      label={inp.label}
                      placeholder="Enter amount"
                      type="number"
                      id={inp.id}
                      required
                    />
                  ))}
                </InputContainer>
              </React.Fragment>
            ))}
          </SectionContainer>
        )}
      </div>

      <StepperNavigation
        isValid={isValid}
        onNext={() => onHandleNavigation('next')}
      />

      <Portal root="tester">
        <Button
          isCircle
          onClick={() => {
            reset({
              ...getValues(),
              liquidation: {
                ...getValues().liquidation,
                ...generateRandomData(),
              },
            });
          }}
        >
          Random
        </Button>
      </Portal>
    </FormProvider>
  );
};

export default Liquidation;
